// src/pages/functions/FunctionWeatherServiceDetailPage.js
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ProductGrid from "../../components/category_page/ProductGrid";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export default function FunctionWeatherServiceDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const goToFilteredProducts = () =>
    navigate("/kategoriak?all=1&functions=idojaras-szolgaltatas");
  const goToBuilder = () =>
    navigate("/builder", { state: { functionIds: ["idojaras-szolgaltatas"] } });

  return (
    <motion.div
      key={location.key}
      className="min-h-screen bg-gradient-to-b from-white to-slate-50 text-[#0c1a26]"
      initial="hidden"
      animate="show"
    >
      {/* ===== HERO – egységes stílus (Home/Builder) ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
        <div className="absolute inset-0 opacity-25">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${"/img/t5.webp"})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-20 md:pb-28 text-white">
          <motion.h1 className="text-3xl md:text-5xl font-bold drop-shadow-sm" variants={fadeUp}>
            Időjárás szolgáltatás
          </motion.h1>
          <motion.p className="text-white/90 text-lg md:text-xl max-w-3xl mt-2" variants={fadeUp}>
            A ház <strong>az időjáráshoz igazodik</strong>: szélre felhúzza az árnyékolót, esőben leállítja
            az öntözést, hőhullám előtt előhűt – előrejelzés és szenzorok alapján. {/* Forrás: előző verzió. */}
          </motion.p>

          <motion.div className="mt-7 flex flex-wrap gap-3" variants={fadeUp}>
            <button
              onClick={goToFilteredProducts}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-3 font-semibold"
            >
              Termékek (előszűrve) →
            </button>
            <button
              onClick={goToBuilder}
              className="inline-flex items-center justify-center rounded-xl border border-white/40 hover:bg-white hover:text-[#0c1a26] px-5 py-3 font-semibold"
            >
              Kezd el a Builderben
            </button>
            <button
              onClick={() => navigate("/funkciok")}
              className="inline-flex items-center justify-center rounded-xl border border-white/40/60 hover:bg-white/10 px-5 py-3 font-semibold"
            >
              Vissza a funkciókhoz
            </button>
          </motion.div>
        </div>
      </section>

      {/* ===== TARTALOM ===== */}
      <div className="max-w-7xl mx-auto px-6 py-10 space-y-14">

        {/* Bevezető + fő kép (egységes minta) */}
        <section className="grid md:grid-cols-[1.3fr_1fr] gap-6 items-stretch">
          <motion.div className="bg-white/90 backdrop-blur rounded-2xl border p-5 md:p-6" variants={fadeUp}>
            <h2 className="text-2xl font-semibold">Mi ez?</h2>
            <p className="text-gray-700 mt-2">
              A rendszer <strong>előre gondolkodik</strong>: a várható időjárás és a helyi szenzorok jelei alapján
              <strong> megelőz és optimalizál</strong>. Kevesebb manuális beavatkozás, <strong>nagyobb nyugalom</strong>.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Viharvédelem</div>
                <div className="text-sm text-gray-700">Széllökésre árnyékolók biztonsági állásban.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Eső-érzékeny öntözés</div>
                <div className="text-sm text-gray-700">Csapadék esetén cikluskihagyás – nincs túllocsolás.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Hőhullám-komfort</div>
                <div className="text-sm text-gray-700">Előhűtés/árnyékolás a kellemes belső klímáért.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Integráció</div>
                <div className="text-sm text-gray-700">Redőny, öntözés, HVAC és jelenetek együtt.</div>
              </div>
            </div>
          </motion.div>

          {/* FŐ KÉP – /img/base/idojaras-szolgaltatas.webp */}
          <motion.figure className="rounded-2xl border overflow-hidden bg-white" variants={fadeUp}>
            <img
              src="/img/base/idojaras-szolgaltatas.webp"
              alt="Időjárás szolgáltatás – fő illusztráció"
              className="w-full h-full object-contain"
              loading="lazy"
            />
            <figcaption className="px-4 py-2 text-xs text-gray-600">
              Időjárás szolgáltatás – előrejelzés + helyi szenzorok vezérlése. {/* Forrás: előző verzió. */}
            </figcaption>
          </motion.figure>
        </section>

        {/* Videók – két rövid MP4, loop + leírás */}
        <section>
          <motion.h2 id="video" className="text-2xl font-semibold mb-3" variants={fadeUp}>
            Hogyan néz ki működés közben?
          </motion.h2>
          <p className="text-sm text-gray-600 mb-4">
            Rövid demók: vihar előtt lép és időjárás-alapú ütemezés – a videók halkan, végtelenítve futnak.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Video #1 */}
            <motion.figure className="rounded-2xl overflow-hidden border bg-black" variants={fadeUp}>
              <video
                className="w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/videos/idojaras-szolgaltatas-1.mp4" type="video/mp4" />
              </video>
              <figcaption className="p-3 bg-white text-sm text-gray-700">
                <strong>Vihar előtt lép:</strong> árnyékolók, fedések és érzékeny elemek <em>biztonságba kerülnek</em> – kevesebb kár, több nyugalom.
              </figcaption>
            </motion.figure>

            {/* Video #2 */}
            <motion.figure className="rounded-2xl overflow-hidden border bg-black" variants={fadeUp}>
              <video
                className="w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/videos/idojaras-szolgaltatas-2.mp4" type="video/mp4" />
              </video>
              <figcaption className="p-3 bg-white text-sm text-gray-700">
                <strong>Okos ütemezés:</strong> öntözés és HVAC a <em>valódi időjáráshoz</em> igazítva – pazarlás helyett megtakarítás.
              </figcaption>
            </motion.figure>
          </div>
        </section>

        {/* Mikor használd? */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-4" variants={fadeUp}>
            Mikor használd?
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Szeles idő</h3>
              <p className="text-gray-700 text-sm">Az árnyékoló automatikusan biztonságos állásba húzódik.</p>
            </motion.div>
            <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Eső közeleg</h3>
              <p className="text-gray-700 text-sm">Az öntözés kihagyja az aktuális ciklust – nincs túllocsolás.</p>
            </motion.div>
            <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Hőhullám</h3>
              <p className="text-gray-700 text-sm">Előhűtés/árnyékolás a komfort és energiahatékonyságért.</p>
            </motion.div>
            <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Fagyveszély</h3>
              <p className="text-gray-700 text-sm">Kerti csap, medence és külső szenzorok fagyvédelme aktiválódik.</p>
            </motion.div>
          </div>
        </section>

        {/* Előnyök és korlátok */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-4" variants={fadeUp}>
            Előnyök és korlátok
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div className="bg-green-50 border border-green-200 rounded-xl p-4" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Előnyök</h3>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>Proaktív védelem és kényelem</li>
                <li>Energiamegtakarítás és vízspórolás</li>
                <li>Rugalmas szabályok és kivételek</li>
              </ul>
            </motion.div>
            <motion.div className="bg-red-50 border border-red-200 rounded-xl p-4" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Korlátok</h3>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>Helyi szenzorok (szél/eső) pontos elhelyezése fontos</li>
                <li>Előrejelzés pontossága szolgáltatótól függ</li>
                <li>Komplexebb logikához kezdeti finomhangolás szükséges</li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Ajánlott termékek – funkció szerint előszűrve (ProductGrid) */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-4" variants={fadeUp}>
            Ajánlott termékek
          </motion.h2>
          <div className="rounded-2xl border bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
            <ProductGrid
              activeFilters={{ functionId: "idojaras-szolgaltatas" }}
              setActiveFilters={() => {}}
              categoriesMap={{}}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={goToFilteredProducts}
              className="rounded-xl bg-blue-600 text-white px-5 py-3 hover:bg-blue-700"
            >
              Termékek teljes listája →
            </button>
            <button
              onClick={goToBuilder}
              className="rounded-xl border px-5 py-3 hover:bg-gray-50"
            >
              Időjárás szolgáltatás építése a Builderben
            </button>
          </div>
        </section>
      </div>

      {/* Sticky mobil CTA */}
      <div className="md:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
        <motion.button
          className="bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg"
          onClick={goToFilteredProducts}
          aria-label="Ugrás az időjárás szolgáltatás termékekhez"
          whileTap={{ scale: 0.98 }}
          whileHover={{ scale: 1.02 }}
        >
          Időjárás termékek →
        </motion.button>
      </div>
    </motion.div>
  );
}
